import React, { FC, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { InputActionButton } from '@ad-scripts/presentation/components/common/input_field';
import InputField, { InputFieldProps } from 'src/presentation/components/forms/InputField';

/**
 * When "stateless" is true, the component does not maintain a separate internal state and delegates
 * state updates to the parent. The parent is responsible for re-rendering of the updates.
 * Used in composite components (e.g. editable table row)
 */
interface WrappedFieldProps extends InputFieldProps {
  allowAutocomplete?: boolean;
  stateless?: boolean;
}

interface MaskedFieldProps {
  isMasked: boolean;
  allowAutocomplete?: boolean;
  mask?: string;
  showClearButton?: boolean;
}

const DEFAULT_FIELD_MASK = '••••••••••';

/**
 * Prevents unnecessary form re-renders on each keystroke by isolating and
 * re-rendering only the input element that changed. Parent form must update
 * the current working state in the onChange(...) handler
 */
export const WrappedField: FC<WrappedFieldProps> = (props): JSX.Element => {
  const { allowAutocomplete = false, onChange, value, stateless, ...rest } = props;
  const [currentState, setCurrentState] = useState(value);
  const autocompleteProps = allowAutocomplete ? {} : { autoComplete: 'new-password' };

  return (
    <StyledField
      {...autocompleteProps}
      {...rest}
      value={stateless ? value : currentState}
      onChange={value => {
        onChange && onChange(value);
        if (stateless) return;
        setCurrentState(value);
      }}
    />
  );
};

/**
 * Displays the placeholder "mask" instead of actual sensitive data and handles various empty/dirty states.
 * If 'isMasked' is false or the field has been edited, then use the regular 'placeholder' property instead
 * (e.g. for token hints)
 *
 * See "https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#preventing_autofilling_with_autocompletenew-password"
 * on why we use "new-password" instead of "off" for autocomplete prevention
 */
export const MaskedField: FC<InputFieldProps & MaskedFieldProps> = (props): JSX.Element => {
  const [state, setState] = useState({ value: props.value, dirty: false });
  const {
    allowAutocomplete = false,
    disabled,
    mask = DEFAULT_FIELD_MASK,
    isMasked,
    onChange,
    placeholder,
    readOnly,
    showClearButton,
    ...rest
  } = props;
  const autocompleteProps = allowAutocomplete ? {} : { autoComplete: 'new-password' };
  const needsMask = mask && isMasked && !state.dirty;
  const inputRef = React.createRef<HTMLInputElement>();
  const needsFocus = useRef(false);

  const showAction = showClearButton && !disabled && !readOnly;
  const actionDisabled = !state.value && state.dirty;
  const onActionClick = () => {
    needsFocus.current = true;
    setValue('');
  };
  const action = showAction ? (
    <InputActionButton disabled={actionDisabled} onClick={onActionClick}>
      Clear
    </InputActionButton>
  ) : null;

  // We want to grab the focus back after the field was cleared by the action button
  useEffect(() => {
    if (needsFocus.current && inputRef.current) {
      inputRef.current.focus();
      needsFocus.current = false;
    }
  });

  const setValue = (newValue: string) => {
    onChange && onChange(newValue);
    setState({ value: newValue, dirty: true });
  };

  return (
    <StyledField
      {...autocompleteProps}
      {...rest}
      action={action}
      disabled={disabled}
      value={state.value}
      masked={needsMask}
      placeholder={needsMask ? mask : placeholder}
      readOnly={readOnly}
      ref={inputRef}
      onChange={setValue}
    />
  );
};

const StyledField = styled(InputField)`
  &&& input {
    background-color: ${p => (p.disabled ? 'inherit' : p.theme.colors.white)};
  }
  &&& input::placeholder {
    color: ${p => (p.masked && !p.disabled ? p.theme.colors.black : p.theme.colors.gray500)};
  }
  &&& ${InputActionButton} {
    color: ${p => p.theme.colors.gray800};
  }
`;
